import { cn } from "@/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes } from "react";
import { useSelectionIrisOrigin } from "@/hooks/use-selection-iris-origin";
import { SelectionIris } from "./SelectionIris";
import { SurfaceProvider, type SurfaceKind } from "./Surface";
import { useElementRef } from "./use-element-ref";

/** Classes of `Card`: surface treatment (`variant`) fed by a palette `tone`; `highlight` rings it in that tone,
 * `accent` draws a 4px left edge in its own tone (status strip); `dimmed` fades and desaturates an unavailable card (`"opacity"` fades only);
 * `stub` is a hairline dashed slip for a code to copy (page-tinted, or paper on `tone="game"`);
 * `selected` (with `elevated`, true or false) makes it selectable: a 2px card-colored rim, and once picked a `SelectionIris`
 * tint in `tone` that opens from the tap point plus a lifted shadow, without changing its box;
 * `emphasis="strong"` (with `tinted`) is the status banner: full-tone border, 10-20% fill; `emphasis="faint"` (with `outline`)
 * a 15% tone hairline on a resting card (the delivery code card); `frame` sets the border width (`thick` 2px code tiles,
 * `none` a borderless tint); `elevation="card"` drops a hero to `shadow-card`. */
export const cardVariants = cva("", {
  variants: {
    variant: {
      elevated: "bg-card text-card-text shadow-card",
      outline: "border bg-card text-card-text",
      tinted: "border text-card-text",
      hero: "shadow-float",
      inset: "bg-page",
      dashed: "border-2 border-dashed bg-card text-card-text",
      stub: "border border-dashed",
      glass: "bg-card/90 text-card-text shadow-card backdrop-blur-sm",
      game: "border border-game-line bg-game-ivory text-game-ink",
      plain: "",
    },
    tone: {
      neutral: "",
      primary: "",
      secondary: "",
      success: "",
      warning: "",
      danger: "",
      game: "",
    },
    padding: {
      none: "",
      xs: "p-2",
      compact: "px-3 py-2",
      sm: "p-3",
      md: "p-4",
      lg: "p-4 sm:p-5",
      xl: "p-5",
      "2xl": "p-6",
    },
    radius: {
      card: "rounded-card",
      sheet: "rounded-sheet",
      none: "rounded-none",
    },
    interactive: {
      true: "transition-shadow duration-base ease-standard hover:shadow-card-hover",
      false: "",
    },
    highlight: {
      true: "ring-2 ring-offset-2 ring-offset-page",
      false: "",
    },
    dimmed: {
      true: "opacity-60 grayscale",
      opacity: "opacity-60",
      false: "",
    },
    selected: {
      true: "transition-shadow duration-base ease-standard",
      false: "transition-shadow duration-base ease-standard",
    },
    emphasis: {
      soft: "",
      strong: "",
      faint: "",
    },
    frame: {
      default: "",
      thick: "border-2",
      none: "border-0",
    },
    elevation: {
      default: "",
      card: "shadow-card",
    },
    accent: {
      none: "",
      neutral: "border-l-4 border-l-border",
      primary: "border-l-4 border-l-primary",
      secondary: "border-l-4 border-l-secondary",
      success: "border-l-4 border-l-success",
      warning: "border-l-4 border-l-warning",
      danger: "border-l-4 border-l-danger",
    },
  },
  compoundVariants: [
    { variant: "outline", tone: "neutral", class: "border-border" },
    { variant: "outline", tone: "primary", class: "border-primary" },
    { variant: "outline", tone: "secondary", class: "border-secondary" },
    { variant: "outline", tone: "success", class: "border-success" },
    { variant: "outline", tone: "warning", class: "border-warning" },
    { variant: "outline", tone: "danger", class: "border-danger" },

    { variant: "dashed", tone: "neutral", class: "border-border" },
    { variant: "dashed", tone: "primary", class: "border-primary/50" },
    { variant: "dashed", tone: "secondary", class: "border-secondary/50" },
    { variant: "dashed", tone: "success", class: "border-success/50" },
    { variant: "dashed", tone: "warning", class: "border-warning/50" },
    { variant: "dashed", tone: "danger", class: "border-danger/50" },
    { variant: "stub", tone: "neutral", class: "border-border bg-page text-card-text" },
    { variant: "stub", tone: "game", class: "border-game-gold-deep bg-game-paper text-game-ink" },

    { highlight: true, tone: "neutral", class: "ring-card-text/20" },
    { highlight: true, tone: "primary", class: "ring-primary" },
    { highlight: true, tone: "secondary", class: "ring-secondary" },
    { highlight: true, tone: "success", class: "ring-success" },
    { highlight: true, tone: "warning", class: "ring-warning" },
    { highlight: true, tone: "danger", class: "ring-danger" },

    { variant: "tinted", tone: "neutral", class: "border-border bg-gray-scale-100" },
    { variant: "tinted", tone: "primary", class: "border-primary/30 bg-primary/5" },
    { variant: "tinted", tone: "secondary", class: "border-secondary/30 bg-secondary/5" },
    { variant: "tinted", tone: "success", class: "border-success/30 bg-success/5" },
    { variant: "tinted", tone: "warning", class: "border-warning/30 bg-warning/5" },
    { variant: "tinted", tone: "danger", class: "border-danger/30 bg-danger/5" },
    { variant: "tinted", emphasis: "strong", tone: "neutral", class: "border-card-text/30 bg-gray-scale-200" },
    { variant: "tinted", emphasis: "strong", tone: "primary", class: "border-primary bg-primary/10" },
    { variant: "tinted", emphasis: "strong", tone: "secondary", class: "border-secondary bg-secondary/10" },
    { variant: "tinted", emphasis: "strong", tone: "success", class: "border-success bg-success/10" },
    { variant: "tinted", emphasis: "strong", tone: "warning", class: "border-warning bg-warning/15" },
    { variant: "tinted", emphasis: "strong", tone: "danger", class: "border-danger bg-danger/20" },

    { variant: "outline", emphasis: "faint", tone: "neutral", class: "border-border shadow-card" },
    { variant: "outline", emphasis: "faint", tone: "primary", class: "border-primary/15 shadow-card" },
    { variant: "outline", emphasis: "faint", tone: "secondary", class: "border-secondary/15 shadow-card" },
    { variant: "outline", emphasis: "faint", tone: "success", class: "border-success/15 shadow-card" },
    { variant: "outline", emphasis: "faint", tone: "warning", class: "border-warning/15 shadow-card" },
    { variant: "outline", emphasis: "faint", tone: "danger", class: "border-danger/15 shadow-card" },

    { variant: "elevated", selected: [true, false], class: "relative isolate border-2 border-card" },
    { variant: "elevated", selected: true, class: "shadow-card-hover" },

    { variant: "hero", tone: "neutral", class: "bg-primary text-primary-foreground" },
    { variant: "hero", tone: "primary", class: "bg-primary text-primary-foreground" },
    { variant: "hero", tone: "secondary", class: "bg-secondary text-secondary-foreground" },
    { variant: "hero", tone: "success", class: "bg-success text-on-media" },
    { variant: "hero", tone: "warning", class: "bg-warning text-warning-foreground" },
    { variant: "hero", tone: "danger", class: "bg-danger text-danger-foreground" },
  ],
  defaultVariants: {
    variant: "elevated",
    tone: "neutral",
    padding: "md",
    radius: "card",
    interactive: false,
    highlight: false,
    dimmed: false,
    accent: "none",
    emphasis: "soft",
    frame: "default",
    elevation: "default",
  },
});

type CardVariant = NonNullable<VariantProps<typeof cardVariants>["variant"]>;
type CardTone = NonNullable<VariantProps<typeof cardVariants>["tone"]>;

const SURFACE_BY_VARIANT: Record<CardVariant, SurfaceKind | null> = {
  elevated: "card",
  outline: "card",
  tinted: "card",
  hero: "card",
  inset: "page",
  dashed: "card",
  stub: "card",
  glass: "card",
  game: "game",
  plain: null,
};

/** Surface a Card variant paints, so text inside resolves its tones against it (`plain` paints none; `tone="game"` paints the game surface). */
export function cardSurface(variant: CardVariant | null | undefined, tone?: CardTone | null): SurfaceKind | null {
  const surface = SURFACE_BY_VARIANT[variant ?? "elevated"];
  return surface && tone === "game" ? "game" : surface;
}

type CardStyleProps = VariantProps<typeof cardVariants>;
type CardToneProps =
  | { tone?: Exclude<NonNullable<CardStyleProps["tone"]>, "game"> | null; variant?: CardStyleProps["variant"] }
  | { tone: "game"; variant: "stub" | "game" };

/** Props of `Card`; `tone="game"` requires `variant` `stub` or `game`, the only ones styled for it; padding comes from `padding`, `className` is for layout only. */
export type CardProps = Omit<VariantProps<typeof cardVariants>, "tone" | "variant"> &
  CardToneProps &
  HTMLAttributes<HTMLElement> & {
    as?: "div" | "section" | "article" | "li" | "aside" | "span";
  };

/** Surface for grouped content (elevated, outline, tinted, hero, inset, dashed, stub, glass, game, plain); provides its text surface. */
export const Card = forwardRef<HTMLElement, CardProps>(function Card(
  { as: Tag = "div", variant, tone, padding, radius, interactive, highlight, dimmed, accent, selected, emphasis, frame, elevation, className, children, onPointerDownCapture, ...rest },
  ref,
) {
  const elementRef = useElementRef(ref);
  const surface = cardSurface(variant, tone);
  const iris = useSelectionIrisOrigin();
  const selectable = selected != null && (variant ?? "elevated") === "elevated";
  return (
    <Tag
      {...rest}
      onPointerDownCapture={(event) => {
        if (selectable) iris.onPointerDownCapture(event);
        onPointerDownCapture?.(event);
      }}
      ref={elementRef}
      className={cn(cardVariants({ variant, tone, padding, radius, interactive, highlight, dimmed, accent, selected, emphasis, frame, elevation }), className)}
    >
      {selectable ? <SelectionIris selected={selected} origin={iris.origin} tone={tone && tone !== "game" ? tone : "neutral"} /> : null}
      {surface ? <SurfaceProvider surface={surface}>{children}</SurfaceProvider> : children}
    </Tag>
  );
});
